import React from 'react';
import PageContainerBlank from '../../components/PageContainer/PageContainerBlank';
import PageEndBlank from '../../components/PageContainer/PageEndBlank';
import Museum from '../../components/Museum/Museum';

const GLOBAL_HISTORY_EXHIBITS = [
	{ // St. Bonaventure at the Council of Lyons in 1274 (by Francisco de Zurbaran)
		image: "https://www.meisterdrucke.uk/kunstwerke/1260px/Francisco%20de%20Zurbaran%20-%20St%20Bonaventure%20%281221-74%29%20at%20the%20Council%20of%20Lyons%20in%201274%201627%20%20-%20%28MeisterDrucke-190941%29.jpg",
		title: "The Second Council of Lyons",
		origin: "1274",
		description: "The Society traces its origin to Lyons II, which called the faithful to develop a special devotion to the Holy Name of Jesus and make reparation for insults against it, particularly those associated with the Albigensian heresy.",
	},
	{ // Blessed John of Vercelli (by Rev. William W. Crandall)
		image: "/static/images/history/BlessedJohn.jpg",
		title: "Blessed John of Vercelli",
		origin: "1205 – November 30, 1283",
		description: "Pope Gregory X selected the Dominicans to preach devotion to the Holy Name via a letter to Blessed John of Vercelli, their master general. Dominican churches thereafter established Holy Name altars and confraternities.",
	}, // https://orderofpreachersindependent.org/wp-content/uploads/2016/11/jonn-v.jpg
	{ // Image of IHS and Latin on a vestment
		image: "https://www.meisterdrucke.uk/kunstwerke/1260px/Titian_%28c1488_1576%29_%28school_of%29_-_Pius_IV_%281499_-_1565%29_Painting_by_Bizzozzero_Milan_Collection_of_Ca_Granda_Hospi_-_%28MeisterDrucke-983261%29.jpg",
		title: "Didacus of Victoria",
		origin: "Unknown – 1450",
		description: "As known from Pope Pius IV's letter dated April 13, 1564, a man named Didacus of Victoria organized his own extremely effective confraternity— the Society of the Holy Name of God. It later received indulgences from the Pontiff and merged with the Society of the Holy Name of Jesus. This merge resulted in a Society much more like the one of today, with a rule and constitution.",
	}, // https://en.wikisource.org/wiki/With_God/Devotion_to_the_Holy_Name_of_Jesus
	{ // First footage of the Pope in history
		image: "https://upload.wikimedia.org/wikipedia/commons/8/83/Papst_Leo_XIII_1898.jpg",
		title: "The Society in America",
		origin: "May 20, 1896 – 1960s",
		description: "Pope Leo XIII's 1896 decree allowed bishops to dispense with the rule that limited each town or city to a single confraternity. This easing opened the way for Holy Name branches to multiply rapidly throughout the United States.",
	},
];

const ST_JOSEPH_HISTORY_EXHIBITS = [
	{
		image: "/static/images/history/HolyNameMen.webp",
		title: "Holy Name Men",
		description: "At its height in the 1960s, there were approximately 250 Holy Name men at St. Joseph. Several of the men, at one time or another, held offices at the national and state levels.",
	},
	{
		image: "/static/images/history/OfficialManual.jpg",
		title: "Holy Name Manual",
		description: "The life of a Holy Name man revolves around the life of the parish. The Holy Name Manual contains the proper manner of living for a Holy Name man.",
	},
	{
		image: "/static/images/history/MinutesBooks.jpg",
		title: "Meeting Minutes",
		description: "The below list of presidents was reconstructed from all the archived meeting minutes. Our paper records range from 1952 to 2012.",
	},
	{
		image: "/static/images/history/PennTwpProclamation.jpg",
		title: "Community Recognition",
		description: "This is a proclamation by the Penn Township Board of Commissioners recognizing the St. Joseph Holy Name Society's 100 year anniversary.",
	},
	{
		image: "/static/images/history/CongressCertificate.jpg",
		title: "Congressional Recognition",
		description: "This is a certificate signed by Todd R. Platts, a former member of the U.S. House of Representatives, given to the St. Joseph Holy Name Society in recognition of the Society's service to the community.",
	},
];

function conditionalItalics(name) {
	if (name === "Unknown") {
		return (<em>{name}</em>);
	}
	return name;
}

function buildTableRows(presidentList, constructedTableRows) {
	const leftLength = Math.floor(presidentList.length/2) + presidentList.length%2;
	const remainingLength = presidentList.length;

	let left = 0;
	let right = leftLength;
	// The left list is either equal size or longer than the right
	for (; left < leftLength;) {
		let leftCellTerm = (<td>{presidentList[left].term}</td>);
		let leftCellName = (<td>{conditionalItalics(presidentList[left].name)}</td>);
		let tableRow;

		if (right < remainingLength) {
			tableRow = (
				<tr>
					{leftCellTerm}
					{leftCellName}
					<td>{presidentList[right].term}</td>
					<td>{conditionalItalics(presidentList[right].name)}</td>
				</tr>
			);
		} else {
			tableRow = (
				<tr>
					{leftCellTerm}
					{leftCellName}
					<td></td>
					<td></td>
				</tr>
			);
		}

		constructedTableRows.push(tableRow);
		left++;
		right++;
	}
}

function buildMobileTableRows(presidentList, constructedTableRows) {
	let first = true;
	for (let president of presidentList) {
		let emptyRow = (<tr><td style={{ height: "5px" }}></td></tr>);
		if (first) { // Eliminate top spacer for first entry
			emptyRow = (null);
			first = false;
		}
		let tableEntry = (
			<tbody>
				{emptyRow}
				<tr><td>{conditionalItalics(president.name)}</td></tr>
				<tr><td>{president.term}</td></tr>
			</tbody>
		);
		constructedTableRows.push(tableEntry);
	}
}

const History = () => {
	const presidentListA = [
		{ term: "1923 – 1924", name: "Vincent Topper" },
		{ term: "1924 – 1925", name: "George Oaster" },
		{ term: "1925 – 1926", name: "W. Guy Lingg" },
	];
	let constructedTableRowsA = [];
	let constructedMobileTableRowsA = [];

	const presidentListB = [
		{ term: "1947 – 1948", name: "William Brenner" },
		{ term: "1948 – 1952", name: "Unknown" },
		{ term: "1952 – 1953", name: "W. Guy Lingg" },
		{ term: "1953 – 1955", name: "Jesse Crabbs, ESQ" },
		{ term: "1955 – 1956", name: "Dr. Reginald Gobel" },
		{ term: "1956 – 1958", name: "Joseph Lawrence" },
		{ term: "1958 – 1959", name: "Richard Gingrow" },
		{ term: "1959 – 1960", name: "Jerome Overbaugh" },
		{ term: "1960 – 1961", name: "William Devine" },
		{ term: "1961 – 1962", name: "Gordon Axworthy" },
		{ term: "1962 – 1963", name: "Raphael Redding" },
		{ term: "1963 – 1964", name: "Germanus Orndorff" },
		{ term: "1964 – 1965", name: "Harry Seifert" },
		{ term: "1965 – 1966", name: "John Neiderer" },
		{ term: "1966 – 1968", name: "Normand Raboin" },
		{ term: "1968 – 1970", name: "Albert Redding" },
		{ term: "1970 – 1974", name: "James Neiderer" },
		{ term: "1974 – 1976", name: "Joseph Tavares" },
		{ term: "1976 – 1978", name: "John Owens" },
		{ term: "1978 – 1980", name: "Richard Arigo" },
		{ term: "1980 – 1982", name: "Chester Barrick" },
		{ term: "1982 – 1984", name: "Eugene Schuchart" },
		{ term: "1984 – 1986", name: "Arthur Binner" },
		{ term: "1986 – 1988", name: "Normand Raboin" },
		{ term: "1988 – 1993", name: "Harry Buckley" }, // Deacon Tom said Harry served 5-8 years, making this an approximation
		{ term: "1993 – 2010", name: "Deacon Tom Aumen" },
		{ term: "2010 – 2016", name: "Richard Arigo" },
		{ term: "2016 – 2017", name: "Robert Gerkin" },
		{ term: "2017 – 2022", name: "Richard Arigo" }, // The HNS did not convene during COVID for fears of the older members
		{ term: "2022 – 2026", name: "Craig Redding" },
	];
	let constructedTableRowsB = [];
	let constructedMobileTableRowsB = [];

	buildTableRows(presidentListA, constructedTableRowsA);
	buildMobileTableRows(presidentListA, constructedMobileTableRowsA);
	buildTableRows(presidentListB, constructedTableRowsB);
	buildMobileTableRows(presidentListB, constructedMobileTableRowsB);

	return (
		<div>
			<PageContainerBlank />

			<h1>History of the Holy Name Society</h1>
			<p className="justify"><span className="tab"></span>The Holy Name Society began in 1274 as the Confraternity of the Most Holy Name of Jesus and grew through Dominican preaching into a worldwide devotion. Across its history, popes repeatedly encouraged the society, regulated its organization, and granted it many indulgences to support its members and work.</p>

			<Museum exhibits={GLOBAL_HISTORY_EXHIBITS} />

			<h1>The Holy Name at St. Joseph</h1>
			<p className="justify"><span className="tab"></span>St. Joseph's Holy Name Society has existed continuously since its inception in 1923; it has functioned as a ministry in the parish ever since. Nearly 3 dozen men have served as President of the Society, following in the holy footsteps of our first President, Vincent Topper.</p>

			<Museum exhibits={ST_JOSEPH_HISTORY_EXHIBITS} />

			<h2>Those Who Served</h2>
			<div className="those-who-served-wrapper">
				<div className="those-who-served-banners">
					<div><img src="/static/images/history/FrontBanner.png" alt="HNS Banner Front" /></div>
					<div><img src="/static/images/history/BackBanner.png" alt="HNS Banner Back" /></div>
				</div>
				<div className="those-who-served-body">
					<table className="desktop-only president-table">
						<tbody>
							{constructedTableRowsA.map((row, index) => (
								<React.Fragment key={index}>{row}</React.Fragment>
							))}
						</tbody>
					</table>
					<table className="mobile-only president-table">
						{constructedMobileTableRowsA.map((row, index) => (
							<React.Fragment key={index}>{row}</React.Fragment>
						))}
					</table>

					<p className="centered">
						<span className="highlight">NO RECORDS ARE AVAILABLE FOR THE YEARS 1926 TO 1947,<br />BUT THE FOLLOWING MEN WERE PRESIDENTS DURING THAT TIME:</span>
						<br />
						<span>Leo Yealy, Walter Kale, F. Raphael Smith, Henry Snyder, W. Guy Lingg, Anthony Strazzella</span>
					</p>

					<table className="desktop-only president-table">
						<tbody>
							{constructedTableRowsB.map((row, index) => (
								<React.Fragment key={index}>{row}</React.Fragment>
							))}
						</tbody>
					</table>
					<table className="mobile-only president-table">
						{constructedMobileTableRowsB.map((row, index) => (
							<React.Fragment key={index}>{row}</React.Fragment>
						))}
					</table>
				</div>
			</div>

			<PageEndBlank />
		</div>
	);
};

export default History;